import { cn } from "@repo/shared/utils";
import type { UserDuplicate } from "@repo/types";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@repo/ui/components/Base/Table";

export interface UsersPreviewTableProps {
  error?: boolean;
  users: UserImportData[];
  roleOverrides?: UserDuplicate[];
  selectedRoleOverrides?: string[];
}

export const UsersPreviewTable = ({
  error = false,
  users,
  roleOverrides,
  selectedRoleOverrides,
}: UsersPreviewTableProps) => {
  return (
    <div className="rounded-md border bg-white">
      <Table wrapperClassName="max-h-[55vh] overflow-auto">
        <UsersPreviewTableHeader error={error} />
        <UsersPreviewTableBody
          users={users}
          roleOverrides={roleOverrides}
          selectedRoleOverrides={selectedRoleOverrides}
        />
      </Table>
    </div>
  );
};

export const UsersPreviewTableHeader = ({ error }: { error?: boolean }) => {
  return (
    <TableHeader
      className={cn("sticky -top-0.5 bg-white", error && "bg-red-300")}
    >
      <TableRow>
        <TableHead className="text-foreground">First name</TableHead>
        <TableHead className="text-foreground">Last name</TableHead>
        <TableHead className="text-foreground">Email</TableHead>
        {/* <TableHead className="text-foreground">Salutation</TableHead> */}
        <TableHead className="text-foreground">Role</TableHead>
        <TableHead className="text-foreground">Address type</TableHead>
        <TableHead className="text-foreground">Street</TableHead>
        <TableHead className="text-foreground">Apt/Suite</TableHead>
        <TableHead className="text-foreground">City</TableHead>
        <TableHead className="text-foreground">State</TableHead>
        <TableHead className="text-foreground">Zip</TableHead>
        <TableHead className="text-foreground">County</TableHead>
      </TableRow>
    </TableHeader>
  );
};

export interface UsersPreviewTableBodyProps {
  users: UserImportData[];
  roleOverrides?: UserDuplicate[];
  selectedRoleOverrides?: string[];
}

export const UsersPreviewTableBody = ({
  users,
  roleOverrides,
  selectedRoleOverrides,
}: UsersPreviewTableBodyProps) => {
  const notOverridenMap = roleOverrides?.reduce(
    (map, dup) => {
      if (!selectedRoleOverrides?.includes(dup.user.email)) {
        map[dup.user.email] = dup;
      }
      return map;
    },
    Object.create(null) as Record<string, UserDuplicate>
  );

  return (
    <TableBody>
      {users.map((u) => {
        const role = notOverridenMap?.[u.email]?.oldRole ?? u.role;

        const cellClassName = u.isUserValid ? "" : "bg-red-300";
        return (
          <TableRow key={`${u.email}${u.firstName}${u.lastName}`}>
            <TableCell className={cellClassName}>{u.firstName}</TableCell>
            <TableCell className={cellClassName}>{u.lastName}</TableCell>
            <TableCell className={cellClassName}>{u.email}</TableCell>
            <TableCell className={cellClassName}>{role}</TableCell>
            {/* <TableCell className={cellClassName}>{u.salutation}</TableCell> */}
            <TableCell className={cellClassName}>
              {u.primaryAddress.type}
            </TableCell>
            <TableCell className={cellClassName}>
              {u.primaryAddress.street}
            </TableCell>
            <TableCell className={cellClassName}>
              {u.primaryAddress.street2}
            </TableCell>
            <TableCell className={cellClassName}>
              {u.primaryAddress.city}
            </TableCell>
            <TableCell className={cellClassName}>
              {u.primaryAddress.state}
            </TableCell>
            <TableCell className={cellClassName}>
              {u.primaryAddress.zip}
            </TableCell>
            <TableCell className={cellClassName}>
              {u.primaryAddress.country}
            </TableCell>
          </TableRow>
        );
      })}
    </TableBody>
  );
};

export type UserImportData = {
  firstName: string;
  lastName: string;
  email: string;
  salutation: string;
  role: string;
  primaryAddress: {
    type: string;
    street: string;
    street2: string;
    city: string;
    state: string;
    zip: string;
    country: string;
  };
  isUserValid: boolean;
  isDuplicate?: boolean;
};
